import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction
} from './actions';

const name = 'admin';

export const getRoles = createAsyncThunk(
  `${name}/getRoles`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/admin/users/roles');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUser = createAsyncThunk(
  `${name}/getUser`,
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/users/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUsers = createAsyncThunk(
  `${name}/getUsers`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/admin/users');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postUser = createAsyncThunk(
  `${name}/postUser`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/admin/users/', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchUser = createAsyncThunk(
  `${name}/patchUser`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/api/admin/users/${data.id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteUser = createAsyncThunk(
  `${name}/deleteUser`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/api/admin/users/${data.id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postUserTenant = createAsyncThunk(
  `${name}/postUserTenant`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `api/admin/tenants/${data.tenantId}/users/`,
        data.data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchUserTenant = createAsyncThunk(
  `${name}/patchUserTenant`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `/api/admin/tenants/${data.tenantId}/users/${data.userId}`,
        data.data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTenants = createAsyncThunk(
  `${name}/getTenants`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/admin/tenants');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTenant = createAsyncThunk(
  `${name}/getTenant`,
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/tenants/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postTenant = createAsyncThunk(
  `${name}/postTenant`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/admin/tenants/', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getLocalesAll = createAsyncThunk(
  `${name}/getLocales`,
  async (data, { rejectWithValue }) => {
    try {
      /* const response = await axios.get(`/api/admin/localizations`);
      
      return response.data; */
      let res = await Promise.all(
        data?.map(async (d) => {
          const response = await axios.get(`/api/localizations/${d}`);
          return { id: d, messages: response.data };
        })
      );
      res = res.reduce((obj, item) => {
        return {
          ...obj,
          [item['id']]: item.messages
        };
      }, {});
      return res;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const setTenants = (state, action) => {
  const tenants = action?.payload;
  state.tenants = tenants;
};

const setTenant = (state, action) => {
  const tenant = action?.payload;
  state.tenant = tenant;
};

const resetAdmin = (state) => {
  window.localStorage.removeItem('access-token');
  axios.defaults.headers.common['Auth'] = null;
  state.errors = false;
  state.tenant = null;
  state.tenants = null;
  state.roles = null;
  state.locales = null;
  state.users = null;
  state.selectedTenantId = null;
};

const adminSlice = createSlice({
  name,
  initialState: {
    loading: false,
    errors: false,
    tenants: null,
    users: null,
    roles: null,
    tenant: null,
    locales: null,
    selectedTenantId: null
  },
  reducers: {
    selectTenant: (state, action) => {
      state.selectedTenantId = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTenant.fulfilled, (state, action) => {
        setTenant(state, action);
      })
      .addCase(getTenants.fulfilled, (state, action) => {
        setTenants(state, action);
      })
      .addCase(getUser.fulfilled, (state, action) => {
        const otherUsers = state.users?.filter(
          (u) => parseInt(u.id, 10) !== parseInt(action.payload.id, 10)
        );
        state.users = [...(otherUsers ? otherUsers : []), action.payload].sort(
          (a, b) => a.id - b.id
        );
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.users = action.payload.sort((a, b) => a.id - b.id);
      })
      .addCase(postUser.fulfilled, (state, action) => {
        state.users = [...state.users, action.payload];
      })
      .addCase(patchUser.fulfilled, (state, action) => {
        state.users = [
          ...state.users.filter((u) => u.id !== action.payload.id),
          action.payload
        ].sort((a, b) => a.id - b.id);
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = [
          ...state.users.filter((u) => u.id !== action.payload.id)
        ].sort((a, b) => a.id - b.id);
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.roles = action.payload;
      })
      .addCase(getLocalesAll.fulfilled, (state, action) => {
        state.locales = action.payload;
      })
      .addCase(postTenant.fulfilled, (state, action) => {
        state.tenants = [...state.tenants, action.payload];
      })
      .addCase(postUserTenant.fulfilled, (state, action) => {
        const otherUsers = state.users.filter(
          (u) => u.id !== action.payload.id
        );
        state.users = [
          ...(otherUsers.length ? otherUsers : []),
          action.payload
        ].sort((a, b) => a.id - b.id);
      })
      .addCase(patchUserTenant.fulfilled, (state, action) => {
        const otherUsers = state.users.filter(
          (u) => u.id !== action.payload.id
        );
        state.users = [
          ...(otherUsers.length ? otherUsers : []),
          action.payload
        ].sort((a, b) => a.id - b.id);
      })
      .addMatcher(isFulfilledAction(name), (state) => {
        state.loading = false;
      })
      .addMatcher(isPendingAction(name), (state) => {
        state.loading = true;
        state.errors = false;
      })
      .addMatcher(isRejectedAction(name), (state, action) => {
        if (action.type !== `${name}/changePassword/rejected`) {
          resetAdmin(state, action);
        }
        state.loading = false;
        state.errors = action.payload;
      });
  }
});

export const { selectTenant } = adminSlice.actions;
export default adminSlice.reducer;
